import MainStorage from 'pixi-project/core/MainStorage';
import {
  RP_COMPARE_FILTER_DATA_CHANGED,
  RP_EVENT_CHECKLIST_CHANGED,
  RP_EVENT_EXPORT_CSV_CONNECTIONS,
  RP_EVENT_EXPORT_CSV_OBJECTS,
  RP_EVENT_LAYER_BTN_CLICKED,
  RP_EVENT_ON_ANALYTICS_CLICKED,
  RP_EVENT_ON_DATAVIEW_MODE_CHANGE,
  RP_EVENT_ON_HAS_ANALYTICS_LOADED,
  RP_EVENT_ON_SHARE_LINK_TOGGLE,
  RP_EVENT_SAVE_TO_PNG,
  RP_FILTER_DATA_CHANGED,
} from 'shared/CSharedEvents';
import posthog from 'posthog-js';
import { FunnelProperties } from 'react-project/Util/FunnelProperties';
import { DATA_MODE_TRAFFIC } from 'shared/CSharedConstants';

const EVENT_OPEN_CANVAS = 'open_canvas';
const EVENT_SET_FILTER = 'set_filter';
const EVENT_SET_COMPARISON = 'set_comparison';
const EVENT_ADDED_WIDGET = 'added_widget';
const EVENT_REMOVED_WIDGET = 'removed_widget';
const EVENT_ADDED_REPORT = 'added_report';
const EVENT_REMOVED_REPORT = 'removed_report';
const EVENT_MODIFIED_REPORT = 'modified_report';
const EVENT_EXPORTED_CANVAS = 'exported_canvas';
const EVENT_ENABLED_ANALYTICS = 'enabled_analytics';
const EVENT_SET_DATAVIEW = 'set_dataview';
const EVENT_MODIFIED_CHECKLIST_ITEM = 'modified_checklist_item';
const EVENT_ADDED_STEP_TO_CANVAS = 'added_step_to_canvas';
const EVENT_ADDED_MAPPING_ITEM = 'added_mapping_item';
const EVENT_MODIFIED_NOTE = 'modified_note';
const EVENT_ENABLED_LAYER = 'enabled_layer';
const EVENT_TOGGLED_SHARING = 'toggled_sharing';
const EVENT_RESTORED_CANVAS_VERSION = 'restored_canvas_version';

export default class EventTrackingController {
  constructor() {
    this.funnelConfiguration = null;
    this.hasAnalytics = null;
    this.addEventListeners();
  }

  addEventListeners() {
    document.addEventListener(
      RP_EVENT_ON_HAS_ANALYTICS_LOADED,
      this.onHasAnalyticsLoaded.bind(this),
    );
    document.addEventListener(RP_FILTER_DATA_CHANGED, this.onSetFilter.bind(this));
    document.addEventListener(RP_COMPARE_FILTER_DATA_CHANGED, this.onSetComparison.bind(this));
    document.addEventListener(RP_EVENT_SAVE_TO_PNG, this.onExportedCanvas.bind(this));
    document.addEventListener(RP_EVENT_EXPORT_CSV_OBJECTS, this.onExportElements.bind(this));
    document.addEventListener(RP_EVENT_EXPORT_CSV_CONNECTIONS, this.onExportConnections.bind(this));
    document.addEventListener(RP_EVENT_ON_ANALYTICS_CLICKED, this.onEnabledAnalytics.bind(this));
    document.addEventListener(RP_EVENT_ON_DATAVIEW_MODE_CHANGE, this.onSetDataview.bind(this));
    document.addEventListener(RP_EVENT_CHECKLIST_CHANGED, this.onModifiedChecklistItem.bind(this));
    document.addEventListener(RP_EVENT_LAYER_BTN_CLICKED, this.onEnabledLayer.bind(this));
    document.addEventListener(RP_EVENT_ON_SHARE_LINK_TOGGLE, this.onToggledSharing.bind(this));
  }

  sendEvent(eventName, data) {
    posthog.capture(eventName, data);
  }

  onHasAnalyticsLoaded(event) {
    this.hasAnalytics = event.detail.hasAnalyticsData;
    if (this.funnelConfiguration !== null && this.hasAnalytics !== null) {
      this.sendOpenCanvasEvent();
    }
  }

  onOpenCanvas(objects, connections, funnelConfiguration) {
    this.funnelConfiguration = funnelConfiguration;
    if (this.funnelConfiguration !== null && this.hasAnalytics !== null) {
      this.sendOpenCanvasEvent();
    }
  }

  sendOpenCanvasEvent() {
    const vl = this.funnelConfiguration ? this.funnelConfiguration.visibleDataLayers || {} : {};

    const has_analytics = MainStorage.getHasAnalyticsData();

    const data = {
      has_analytics: has_analytics,
      is_checklist_layer_enabled: vl.checklist,
      is_flow_layer_enabled: vl.flow,
      is_forecast_layer_enabled: vl.forecasting,
      is_lead_time_layer_enabled: vl.leadTime,
      is_notes_layer_enabled: vl.notes,
      is_numbers_layer_enabled: vl.numbers,
    };

    const ownerID = FunnelProperties.getOwnerId();
    const workspaceId = this.funnelConfiguration.projectId;

    posthog.group('project', workspaceId, {
      owner_id: ownerID,
    });

    this.sendEvent(EVENT_OPEN_CANVAS, data);
  }

  onSetFilter(e) {
    const data = e.detail;
    this.sendEvent(EVENT_SET_FILTER, data);
  }

  onSetComparison(e) {
    const data = e.detail;
    this.sendEvent(EVENT_SET_COMPARISON, data);
  }

  onAddedWidget(e) {
    const type = e.detail.object.type;

    const data = {
      type: type,
    };

    this.sendEvent(EVENT_ADDED_WIDGET, data);
  }

  onRemovedWidget(widget) {
    const type = widget.type;
    this.sendEvent(EVENT_REMOVED_WIDGET, { type: type });
  }

  onAddedReport(reportView) {
    this.sendEvent(EVENT_ADDED_REPORT);
  }

  onRemovedReport(reportView) {
    this.sendEvent(EVENT_REMOVED_REPORT);
  }

  onModifiedReport(action) {
    this.sendEvent(EVENT_MODIFIED_REPORT, { action: action });
  }

  onExportedCanvas(e) {
    const data = {
      type: 'png',
      resolution: e.detail.resolution,
    };
    this.sendEvent(EVENT_EXPORTED_CANVAS, data);
  }

  onExportElements() {
    const data = {
      type: 'csv',
      content: 'elements',
    };

    this.sendEvent(EVENT_EXPORTED_CANVAS, data);
  }

  onExportConnections() {
    const data = {
      type: 'csv',
      content: 'connections',
    };

    this.sendEvent(EVENT_EXPORTED_CANVAS, data);
  }

  onEnabledAnalytics(e) {
    const data = e.detail;
    this.sendEvent(EVENT_ENABLED_ANALYTICS, data);
  }

  onSetDataview(e) {
    const data = {
      type: e.detail === DATA_MODE_TRAFFIC ? 'traffic' : 'funnel',
    };
    this.sendEvent(EVENT_SET_DATAVIEW, data);
  }

  onModifiedChecklistItem(e) {
    const scope = e.detail.id ? 'step' : 'global';
    const action = e.detail.action;
    const items = e.detail.checklistData.items;

    function countItems(items) {
      let count = 0;
      items.forEach((item) => {
        count += 1;
        if (item.items && item.items.length > 0) {
          count += countItems(item.items);
        }
      });
      return count;
    }

    const number_of_items = countItems(items);

    const data = {
      action: action,
      number_of_items: number_of_items,
      scope: scope,
    };

    posthog.capture(EVENT_MODIFIED_CHECKLIST_ITEM, data);
  }

  onAddedStepToCanvas(detail) {
    const source = detail.eventSource;
    const type = detail.explorerType;

    const data = {
      integration: null,
      source: source,
      type: type,
    };

    if (detail.object && detail.object.filterData) {
      const integrationFilter = detail.object.filterData.find(
        (filter) => filter.key === '__dataOrigin__',
      );
      if (integrationFilter) {
        data.integration =
          integrationFilter.value === 'hubspot' || integrationFilter.value === 'gohighlevel'
            ? integrationFilter.value
            : null;
      }
    }

    this.sendEvent(EVENT_ADDED_STEP_TO_CANVAS, data);
  }

  onAddedMappingItem(object) {
    const type = object.category.toLowerCase();
    this.sendEvent(EVENT_ADDED_MAPPING_ITEM, { type: type });
  }

  onModifiedNote(data) {
    this.sendEvent(EVENT_MODIFIED_NOTE, data);
  }

  onEnabledLayer(e) {
    const isActive = e.detail.isActive;
    const type = e.detail.type.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

    if (isActive) {
      this.sendEvent(EVENT_ENABLED_LAYER, { type: type });
    }
  }

  onToggledSharing(e) {
    const data = e.detail;
    this.sendEvent(EVENT_TOGGLED_SHARING, data);
  }

  onRestoredCanvasVersion() {
    this.sendEvent(EVENT_RESTORED_CANVAS_VERSION);
  }
}
